import React, { useState } from "react";
import { MapPin, Search, PartyPopper, XCircle, Send, CheckCircle2, Phone } from "lucide-react";
import { servedZips, company } from "../mock";
import { createLead } from "../api";

// Reusable ZIP availability checker with a simple celebratory lead form
// (name, email, phone). variant: "card" (hero) | "dark" (service areas).
const ZipChecker = ({ variant = "card" }) => {
  const isDark = variant === "dark";
  const [zip, setZip] = useState("");
  const [status, setStatus] = useState("idle"); // idle | invalid | served | notserved | done
  const [city, setCity] = useState("");
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [sending, setSending] = useState(false);

  const checkZip = (e) => {
    e.preventDefault();
    const clean = zip.trim();
    if (!/^\d{5}$/.test(clean)) {
      setStatus("invalid");
      return;
    }
    const matched = servedZips[clean];
    if (matched) {
      setCity(matched);
      setStatus("served");
    } else {
      setStatus("notserved");
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.phone.trim() || !lead.email.trim()) return;
    setSending(true);
    try {
      await createLead({
        name: lead.name,
        email: lead.email,
        phone: lead.phone,
        zip,
        city,
        source: "zip-checker",
      });
    } catch (err) {
      // Non-blocking: still confirm so the user isn't stuck.
    }
    setSending(false);
    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-16468983954/-i7QCLCii8EcEJKBg609",
      });
    }
    setStatus("done");
  };

  const reset = () => {
    setZip("");
    setCity("");
    setLead({ name: "", email: "", phone: "" });
    setStatus("idle");
  };

  const inputBase = isDark
    ? "bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-orange"
    : "bg-brand-cream border-black/10 text-brand-navy placeholder:text-slate-400 focus:border-brand-orange";
  const titleColor = isDark ? "text-white" : "text-brand-navy";
  const subColor = isDark ? "text-white/70" : "text-slate-500";

  return (
    <div
      className={
        isDark
          ? "w-full max-w-md mx-auto"
          : "bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm border border-black/5"
      }
    >
      {status === "done" ? (
        /* Thank-you confirmation */
        <div className="text-center py-4 animate-fade-up" data-testid="zip-done">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h3 className={`font-display font-bold text-xl ${titleColor}`}>
            You're all set, {lead.name.split(" ")[0]}!
          </h3>
          <p className={`text-sm mt-2 ${subColor}`}>
            A Fab Build Pro specialist will contact you shortly to schedule your{" "}
            <span className="font-semibold text-brand-orange">free inspection</span>{" "}
            in {city}.
          </p>
          <button
            onClick={reset}
            data-testid="zip-check-another"
            className={`mt-4 text-sm font-semibold underline ${isDark ? "text-white/80" : "text-brand-navy"}`}
          >
            Check another ZIP
          </button>
        </div>
      ) : status === "served" ? (
        /* Celebratory mini lead form: name, email, phone */
        <div className="animate-fade-up" data-testid="zip-served">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <PartyPopper className="w-5 h-5 text-brand-orange" />
              <h3 className={`font-display font-bold text-lg ${titleColor}`}>
                Congrats — we serve {city}!
              </h3>
            </div>
            <button
              onClick={reset}
              aria-label="Close"
              data-testid="zip-close"
              className={`shrink-0 -mt-1 -mr-1 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                isDark ? "text-white/60 hover:text-white hover:bg-white/10" : "text-slate-400 hover:text-brand-navy hover:bg-black/5"
              }`}
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
          <p className={`text-sm mb-4 ${subColor}`}>
            Leave your details and we'll call to book your{" "}
            <strong>free estimate</strong>.
          </p>
          <form onSubmit={submitLead} className="space-y-2.5">
            <input
              type="text"
              value={lead.name}
              onChange={(e) => setLead({ ...lead, name: e.target.value })}
              placeholder="Your name"
              data-testid="zip-name"
              className={`w-full rounded-xl px-4 py-3 text-[15px] outline-none border transition-colors ${inputBase}`}
              required
            />
            <input
              type="email"
              value={lead.email}
              onChange={(e) => setLead({ ...lead, email: e.target.value })}
              placeholder="Email address"
              data-testid="zip-email"
              className={`w-full rounded-xl px-4 py-3 text-[15px] outline-none border transition-colors ${inputBase}`}
              required
            />
            <input
              type="tel"
              value={lead.phone}
              onChange={(e) => setLead({ ...lead, phone: e.target.value })}
              placeholder="Phone number"
              data-testid="zip-phone"
              className={`w-full rounded-xl px-4 py-3 text-[15px] outline-none border transition-colors ${inputBase}`}
              required
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="zip-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangedark text-white font-semibold py-3 rounded-xl shadow-md transition-colors disabled:opacity-60"
            >
              <Send className="w-4 h-4" />
              {sending ? "Sending..." : "Claim My Free Estimate"}
            </button>
          </form>
          <button
            onClick={reset}
            className={`mt-3 text-xs font-medium underline w-full text-center ${subColor}`}
          >
            Use a different ZIP code
          </button>
        </div>
      ) : (
        /* ZIP input (idle / invalid / notserved) */
        <>
          <div className="flex items-center gap-2 mb-1.5">
            <MapPin className="w-5 h-5 text-brand-orange" />
            <h3 className={`font-display font-bold text-lg ${titleColor}`}>
              Do we serve your area?
            </h3>
          </div>
          <p className={`text-sm mb-4 ${subColor}`}>
            Enter your ZIP code to check availability across the DMV.
          </p>

          <form onSubmit={checkZip} className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={zip}
              data-testid="zip-input"
              onChange={(e) => {
                setZip(e.target.value.replace(/\D/g, ""));
                if (status !== "idle") setStatus("idle");
              }}
              placeholder="e.g. 22201"
              className={`flex-1 rounded-xl px-4 py-3 text-[15px] outline-none border transition-colors ${inputBase}`}
            />
            <button
              type="submit"
              data-testid="zip-check-btn"
              className="bg-brand-orange hover:bg-brand-orangedark text-white rounded-xl px-4 flex items-center justify-center shadow-md transition-colors"
              aria-label="Check ZIP code"
            >
              <Search className="w-5 h-5" />
            </button>
          </form>

          {status === "invalid" && (
            <p className="mt-3 text-sm text-brand-orange font-medium animate-fade-up">
              Please enter a valid 5-digit ZIP code.
            </p>
          )}

          {status === "notserved" && (
            <div className="mt-4 flex items-start gap-2 rounded-xl p-3 text-sm bg-orange-50 text-brand-orangedark border border-orange-200 animate-fade-up">
              <XCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">We may still cover you!</p>
                <p className="text-orange-700 flex items-center gap-1 mt-0.5">
                  <Phone className="w-3.5 h-3.5" />
                  Call{" "}
                  <a href={company.phoneHref} className="underline font-semibold">
                    {company.phone}
                  </a>{" "}
                  to confirm.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ZipChecker;
